/**
 * The send console for one campaign.
 *
 * Nothing is delivered from here. Each message opens in the user's own mail
 * client, WhatsApp or SMS app, and the console records what happened. That is
 * an honest hand-off rather than a delivery pipeline the workspace does not
 * have — and it is why "sent" is something a person marks, not something the
 * app claims.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import Icon from '../../lib/icons.jsx';
import { digitsOf, whatsappLink, download } from '../../lib/format.js';
import { channelLabel, channelIcon } from '../../constants/marketing.js';
import { reachSummary, applyMerge, campaignRecipients } from '../../domain/marketing.js';
import Sheet, { Field } from '../Sheet.jsx';
import { looksHtml, htmlToText } from './CampaignEditor.jsx';
import { Avatar, EmptyState, Spinner } from '../Primitives.jsx';

export default function SendConsole({ id, onClose }) {
  const {
    S, mkt, mktCtx, markSend, markAllSent, recordSends, addOptOut, logMarketingTouch, toast
  } = useApp();

  const [sending, setSending] = useState(false);

  const campaign = mkt.campaigns.find(c => c.id === id);
  const audience = campaign ? mkt.audiences.find(a => a.id === campaign.audience) : null;
  const { ok: list, notes } = campaign
    ? reachSummary(campaignRecipients(campaign, mktCtx), campaign.channel, mktCtx)
    : { ok: [], notes: [] };

  const footer = (S.mkt || {}).footer !== false ? '\n\nReply STOP to opt out.' : '';

  if (!campaign) return null;

  if (!list.length) {
    const targeted = audience || (campaign.contacts || []).length;
    return (
      <Sheet icon="send" tone="g-amber" title={campaign.name} onClose={onClose}>
        <Field label="" wide>
          <EmptyState icon="target" tone="g-amber" title={targeted ? 'Nobody is reachable' : 'Nobody chosen yet'}>
            {targeted
              ? `Nobody here can be reached on ${channelLabel(campaign.channel)}`
                + (notes.length ? ` — ${notes.join(', ')}.` : '.')
              : 'Open the campaign and choose a segment or some contacts first.'}
          </EmptyState>
        </Field>
      </Sheet>
    );
  }

  const sends = campaign.sends || [];
  const sentIds = new Set(sends.filter(s => s.status === 'sent').map(s => s.rid));
  const skipIds = new Set(sends.filter(s => s.status === 'skipped').map(s => s.rid));
  const handled = list.filter(r => sentIds.has(r.id) || skipIds.has(r.id)).length;
  const pct = Math.round(handled / list.length * 100);

  /*
   * A campaign written in HTML is for a real mail provider. Nothing here can
   * deliver HTML — mailto:, wa.me and sms: all carry text — so the hand-off
   * gets a flattened copy while the CSV export keeps the markup intact.
   */
  const source = String(campaign.body || '');
  const flat = looksHtml(source) ? htmlToText(source) : source;

  const messageFor = r => applyMerge(flat, r) + footer;
  const exportFor = r => applyMerge(source, r) + footer;
  const subjectFor = r => applyMerge(campaign.subject || '', r);

  const record = (r, status) => {
    markSend(campaign.id, r.id, status, list.length);
    if (status === 'sent') {
      logMarketingTouch(r.id, campaign.channel, subjectFor(r) || campaign.name, `Campaign: ${campaign.name}`);
    }
  };

  const send = r => {
    const message = messageFor(r);

    if (campaign.channel === 'whatsapp') {
      if (!digitsOf(r.phone)) { toast('No phone number'); return; }
      window.open(whatsappLink(r.phone, message), '_blank');
    } else if (campaign.channel === 'email') {
      window.open(
        `mailto:${encodeURIComponent(r.email || '')}`
        + `?subject=${encodeURIComponent(subjectFor(r))}&body=${encodeURIComponent(message)}`,
        '_blank'
      );
    } else if (campaign.channel === 'sms') {
      const limit = (S.mkt || {}).smsLimit || 160;
      window.open(`sms:${digitsOf(r.phone)}?body=${encodeURIComponent(message.slice(0, limit))}`, '_blank');
    } else {
      if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
        toast('Enable push for this device in Channel setup first');
        return;
      }
      try {
        // eslint-disable-next-line no-new
        new Notification(subjectFor(r) || campaign.name, { body: message.slice(0, 180), tag: campaign.id + r.id });
      } catch { /* blocked */ }
    }
    record(r, 'sent');
  };

  const copy = (text, message = 'Copied') => {
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast(message), () => toast('Copy failed'));
  };

  const optOut = r => {
    addOptOut(String(r.email || digitsOf(r.phone)));
    markSend(campaign.id, r.id, 'skipped', list.length);
    toast(`${r.name} will not be contacted again`);
  };

  /**
   * Hand the whole list to the server in one go, for the channels that have a
   * provider behind them: email through SMTP, SMS through the configured
   * gateway. WhatsApp has no API here, so it keeps the per-recipient hand-off.
   *
   * The server reports per recipient, and only the ones it actually accepted
   * are recorded as sent — a partial failure leaves the rest exactly as they
   * were, ready to retry.
   */
  const sendAll = async () => {
    const pending = list.filter(r => !sentIds.has(r.id) && !skipIds.has(r.id));
    if (!pending.length) { toast('Everyone here has been handled already'); return; }

    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Send this ${channelLabel(campaign.channel)} to ${pending.length} `
      + `recipient${pending.length > 1 ? 's' : ''} now?`
    )) return;

    setSending(true);
    try {
      const M = S.mkt || {};
      const isMail = campaign.channel === 'email';
      // Only SMS is length-capped; WhatsApp carries the message whole.
      const limit = campaign.channel === 'sms' ? (M.smsLimit || 160) : Infinity;

      const out = await api.marketing.send({
        campaignId: campaign.id,
        channel: campaign.channel,
        // The mailbox the campaign was written to go out as. The server keeps
        // the rest of the list behind it, so a refusal still falls over.
        accountId: campaign.accountId || undefined,
        throttleMs: Number(M.throttle || 0) * 1000,
        // Channel setup decides how this goes out, not the server's .env.
        fromName: M.fromName,
        fromEmail: M.fromEmail,
        replyTo: M.replyTo,
        sender: M.smsSender,
        template: campaign.waTemplate || M.waTemplate,
        templateLang: campaign.waTemplateLang || M.waTemplateLang,
        templateVars: (campaign.waFields || []).length || M.waTemplateVars,
        messages: pending.map(r => ({
          rid: r.id,
          to: isMail ? r.email : r.phone,
          subject: isMail ? subjectFor(r) : undefined,
          // The console shows SMS cut at the same limit the hand-off uses, so
          // what is sent here matches what was previewed there.
          text: isMail ? messageFor(r) : messageFor(r).slice(0, limit),
          html: isMail && looksHtml(source) ? applyMerge(source, r) : undefined,
          // A WhatsApp template's blanks, filled in for this person.
          fields: campaign.channel === 'whatsapp'
            ? (campaign.waFields || []).map(f => applyMerge(String(f || ''), r).trim())
            : undefined,
          header: campaign.channel === 'whatsapp' && campaign.waHeaderUrl
            ? { type: (campaign.waHeader && campaign.waHeader.format) || 'image', url: campaign.waHeaderUrl }
            : undefined
        }))
      });

      /* Everything the server said, kept — the refusals are the half of a send
         log that is worth having. */
      const via = (out.accounts || [])[0] || '';
      recordSends(campaign.id, (out.results || []).map(r => ({ ...r, via })), list.length);

      (out.results || []).forEach(result => {
        if (!result.ok) return;
        const who = pending.find(r => r.id === result.rid);
        if (who) {
          logMarketingTouch(
            who.id, campaign.channel, subjectFor(who) || campaign.name,
            `Campaign: ${campaign.name}`);
        }
      });

      const failed = out.failed || 0;
      toast(failed
        ? `Sent ${out.sent}, ${failed} could not be delivered`
        : `Sent ${out.sent} message${out.sent > 1 ? 's' : ''}${via ? ` through ${via}` : ''}`);

      /* A fallback that had to step in is worth saying out loud — it is the
         only sign that the first mailbox is in trouble. */
      (out.failover || []).forEach(f => toast(`${f.from} refused it — ${f.error}`));

      /*
       * Nothing left to do here, so stop sitting on the screen.
       *
       * The console is a worklist. Once every name on it has been handled the
       * list is empty and the only thing left is to close it by hand, which
       * reads as the send not having finished. It stays open when somebody was
       * refused: that is a list of what to do next, not a job done.
       */
      const refused = (out.results || []).filter(r => !r.ok).length;
      if (!refused && pending.length >= list.filter(r => !skipIds.has(r.id)).length) {
        onClose();
        return;
      }
    } catch (e) {
      toast(e.message);
    }
    setSending(false);
  };

  const exportCSV = () => {
    const q = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const lines = [['Name', 'Email', 'Phone', 'Subject', 'Message'].map(q).join(',')]
      .concat(list.map(r => [r.name, r.email, r.phone, subjectFor(r), exportFor(r)].map(q).join(',')));
    download(
      new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      `campaign-${campaign.name.replace(/\W+/g, '-').toLowerCase()}.csv`
    );
    toast(`Exported ${list.length} personalised messages`);
  };

  return (
    <Sheet
      icon={channelIcon(campaign.channel)}
      tone="g-pink"
      title={`${campaign.name} · ${channelLabel(campaign.channel)}`}
      onClose={onClose}
    >
      <Field label="" wide>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
          <span><b>{handled}</b> of {list.length} handled</span>
          <span className="dim">{sentIds.size} sent · {skipIds.size} skipped</span>
        </div>
        <div className="track"><div className="fill" style={{ width: `${pct}%` }} /></div>
      </Field>

      <Field label="" wide>
        <div className="rowb">
          <button
            type="button"
            className="btn sm"
            onClick={() => copy(list.map(r =>
              `— ${r.name} (${r.email || r.phone || ''})\n`
              + (campaign.subject ? `Subject: ${subjectFor(r)}\n` : '')
              + messageFor(r)).join('\n\n'), `All ${list.length} copied`)}
          >
            Copy every message
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => copy(list
              .map(r => (campaign.channel === 'email' ? r.email : r.phone))
              .filter(Boolean).join(', '), 'Addresses copied')}
          >
            Copy the address list
          </button>
          <button type="button" className="btn sm" onClick={exportCSV}>Export for your provider</button>
          {['email', 'sms', 'whatsapp'].includes(campaign.channel) && !isOffline() ? (
            <button type="button" className="btn sm primary" onClick={sendAll} disabled={sending}>
              {sending
                ? <><Spinner />Sending…</>
                : <><Icon name="send" />Send all by {channelLabel(campaign.channel)}</>}
            </button>
          ) : null}
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (!window.confirm(`Mark all ${list.length} as sent?`)) return;
              markAllSent(campaign.id, list.map(r => r.id));
            }}
          >
            Mark everything sent
          </button>
        </div>
      </Field>

      <Field label="" wide>
        <div className="sendlist">
          {list.map(r => {
            const isSent = sentIds.has(r.id);
            const isSkipped = skipIds.has(r.id);
            const message = messageFor(r);

            return (
              <div className="ob" style={{ margin: 0 }} key={r.id}>
                <div className="obh">
                  <Avatar name={r.name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{r.name}</b>
                    <div className="sub">
                      {campaign.channel === 'email' ? (r.email || 'no email') : (r.phone || 'no number')}
                    </div>
                  </div>
                  {isSent ? <span className="chan whatsapp">sent</span> : null}
                  {isSkipped ? <span className="chan rule">skipped</span> : null}
                </div>

                {campaign.channel === 'email' && campaign.subject ? (
                  <div className="dim" style={{ fontSize: 12, marginBottom: 5 }}>{subjectFor(r)}</div>
                ) : null}

                <div style={{
                  fontSize: 12.5, whiteSpace: 'pre-wrap', lineHeight: 1.5,
                  color: 'var(--ink2)', marginBottom: 9
                }}
                >
                  {message.slice(0, 240)}{message.length > 240 ? '…' : ''}
                </div>

                <div className="obacts">
                  <button type="button" className="btn sm primary" onClick={() => send(r)}>
                    <Icon name={channelIcon(campaign.channel)} />
                    {campaign.channel === 'email' ? 'Open in mail'
                      : campaign.channel === 'whatsapp' ? 'WhatsApp'
                        : campaign.channel === 'sms' ? 'Open SMS' : 'Push'}
                  </button>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => copy((campaign.subject ? `Subject: ${subjectFor(r)}\n\n` : '') + message)}
                  >
                    Copy
                  </button>
                  <button type="button" className="btn sm" onClick={() => record(r, 'sent')}>Mark sent</button>
                  <button
                    type="button"
                    className="btn sm"
                    onClick={() => markSend(campaign.id, r.id, 'skipped', list.length)}
                  >
                    Skip
                  </button>
                  <button type="button" className="btn sm danger" onClick={() => optOut(r)}>Opt out</button>
                </div>
              </div>
            );
          })}
        </div>
      </Field>
    </Sheet>
  );
}
